import React, { useState } from "react";
import { motion } from "framer-motion";
import { TextField, Container, Typography, Alert } from "@mui/material";
import catImage from "../images/homecat.png";
import dogImage from "../images/homeimg.png";
import Navbar from "./Navbar.js";
import axiosInstance from "../api/axios";
import { useLocation, useNavigate } from "react-router-dom";

const ResetPassword = () => {
  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const buttonVariants = {
    hover: { backgroundColor: "#BDA697", transition: { duration: 0.3 } },
  };
  const imageVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 1 } },
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const query = new URLSearchParams(location.search);
  const token = query.get("token");

  const onChangePassword = (e) => setNewPassword(e.target.value);
  const onChangeConfirmPassword = (e) => setConfirmPassword(e.target.value);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrors({});

    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match!" });
      return;
    }

    try {
      const response = await axiosInstance.post("/reset-password", {
        token,
        new_password: newPassword,
      });
      setMessage("Password updated successfully!");
      navigate("/login");
    } catch (err) {
      setErrors(err.response?.data.errors || {});
      setMessage(
        err.response?.data.message ||
          "Something went wrong. Please try again later."
      );
    }
  };

  return (
    <section className="relative text-center mt-20 pt-16">
      <Navbar />
      <Container maxWidth="sm" className="mt-10">
        <motion.h1
          variants={textVariants}
          initial="hidden"
          animate="visible"
          className="text-5xl font-bold text-gray-900"
        >
          Reset Your Password
        </motion.h1>
        <motion.p
          variants={textVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="mt-4 text-xl poppins-text text-gray-600"
        >
          Enter your new password to reset your account's password.
        </motion.p>

        {message && (
          <Alert severity={errors.email ? "error" : "success"} className="mb-4">
            {message}
          </Alert>
        )}

        <form onSubmit={onSubmit} className="mt-6 text-left">
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.6 }}
            className="mb-4"
          >
            <Typography variant="h6" className="mb-2 text-left ">
              New Password
            </Typography>
            <TextField
              placeholder="New Password"
              type="password"
              variant="outlined"
              fullWidth
              value={newPassword}
              onChange={onChangePassword}
              className="mb-4"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                },
              }}
            />
          </motion.div>
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.8 }}
            className="mb-4"
          >
            <Typography variant="h6" className="mb-2 text-left">
              Confirm Password
            </Typography>
            <TextField
              placeholder="Confirm Password"
              type="password"
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={onChangeConfirmPassword}
              className="mb-4"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                },
              }}
            />
          </motion.div>

          {errors.confirmPassword && (
            <p className="text-sm poppins-text text-red-500 mt-1">
              {errors.confirmPassword}
            </p>
          )}

          <motion.div className="mt-6 flex justify-center">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              className="mt-6 text-xl px-6 py-3 bg-[#967d6c] text-white font-bold rounded-full  w-full"
            >
              Reset Password
            </motion.button>
          </motion.div>
        </form>
      </Container>

      <motion.div
        className="absolute left-0 top-20 hidden xl:block"
        variants={imageVariants}
        initial="hidden"
        animate="visible"
      >
        <img src={catImage} alt="Cat" className=" h-[36rem] mx-auto" />
        <div className="absolute left-60 bottom-20 transform -translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute left-64 top-80 transform translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute right-0 top-24 hidden xl:block"
        variants={{
          ...imageVariants,
          hidden: { opacity: 0, x: 100 },
        }}
        initial="hidden"
        animate="visible"
      >
        <img src={dogImage} alt="Dog" className="h-[34rem]  mx-auto" />
        <div className="absolute right-24 bottom-2 transform -translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute right-64 bottom-16 transform translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ResetPassword;
